import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
    Card,
    CardContent,
    IconButton,
    Typography,
    Box,
    Divider,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    useTheme,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import SaveIcon from '@material-ui/icons/Save'
import CancelIcon from '@material-ui/icons/Cancel'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import { usePropertyTokens } from '../../../hooks/usePropertyTokens'
import { usePropertyDetails } from '../../../hooks/usePropertyDetails'

export const TokenManagement: React.FC = () => {
    const theme = useTheme()
    const { id } = useParams<{ id: string }>()
    const { property } = usePropertyDetails(id || '')
    const {
        tokenBalance,
        isLoading,
        updateTokensPerUnit,
        grantTokens,
        subtractTokens,
    } = usePropertyTokens(id || '')

    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [grantDialogOpen, setGrantDialogOpen] = useState(false)
    const [subtractDialogOpen, setSubtractDialogOpen] = useState(false)

    const [editTokensPerUnit, setEditTokensPerUnit] = useState('')
    const [grantAmount, setGrantAmount] = useState('')
    const [grantComment, setGrantComment] = useState('')
    const [subtractAmount, setSubtractAmount] = useState('')
    const [subtractComment, setSubtractComment] = useState('')

    // Initialize edit value when balance loads
    useEffect(() => {
        if (tokenBalance) {
            setEditTokensPerUnit(
                tokenBalance.tokens_per_unit_allocated.toString(),
            )
        }
    }, [tokenBalance])

    if (!property) {
        return (
            <Box p={3}>
                <Typography>Loading property...</Typography>
            </Box>
        )
    }

    if (isLoading) {
        return (
            <Box p={3}>
                <Typography>Loading token information...</Typography>
            </Box>
        )
    }

    const billableUnits = property.billable_unit_count || 0
    const tokensPerUnit = tokenBalance?.tokens_per_unit_allocated || 15
    const monthlyTokenGrant = billableUnits * tokensPerUnit
    const currentBalance = tokenBalance?.current_balance || 0

    const handleEditClick = () => {
        if (tokenBalance) {
            setEditTokensPerUnit(
                tokenBalance.tokens_per_unit_allocated.toString(),
            )
        }
        setEditDialogOpen(true)
    }

    const handleSaveTokensPerUnit = async () => {
        const newTokensPerUnit = parseInt(editTokensPerUnit)

        if (isNaN(newTokensPerUnit) || newTokensPerUnit < 0) {
            alert('Please enter a valid number for tokens per unit')
            return
        }

        try {
            await updateTokensPerUnit.mutateAsync(newTokensPerUnit)
            setEditDialogOpen(false)
        } catch (error) {
            console.error('Failed to update tokens per unit:', error)
        }
    }

    const handleGrantTokens = async () => {
        const amount = parseInt(grantAmount)

        if (isNaN(amount) || amount <= 0) {
            alert('Please enter a valid positive number for tokens')
            return
        }

        try {
            await grantTokens.mutateAsync({
                tokens: amount,
                comment: grantComment || undefined,
            })
            setGrantDialogOpen(false)
            setGrantAmount('')
            setGrantComment('')
        } catch (error) {
            console.error('Failed to grant tokens:', error)
        }
    }

    const handleSubtractTokens = async () => {
        const amount = parseInt(subtractAmount)

        if (isNaN(amount) || amount <= 0) {
            alert('Please enter a valid positive number for tokens')
            return
        }

        if (amount > currentBalance) {
            alert(
                `Cannot subtract ${amount} tokens. Current balance is only ${currentBalance} tokens.`,
            )
            return
        }

        try {
            await subtractTokens.mutateAsync({
                tokens: amount,
                comment: subtractComment || undefined,
            })
            setSubtractDialogOpen(false)
            setSubtractAmount('')
            setSubtractComment('')
        } catch (error) {
            console.error('Failed to subtract tokens:', error)
        }
    }

    const formatNumber = (num: number): string => {
        return num.toLocaleString()
    }

    return (
        <Box p={3}>
            <Paper elevation={2} style={{ padding: theme.spacing(3) }}>
                {/* Token Balance Display */}
                <Box mb={3}>
                    <Typography variant="h5" gutterBottom>
                        Token Management
                    </Typography>
                    <Card
                        elevation={1}
                        style={{
                            backgroundColor:
                                currentBalance >= 0
                                    ? theme.palette.success.light
                                    : theme.palette.error.light,
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                gutterBottom
                            >
                                Remaining Tokens
                            </Typography>
                            <Typography
                                variant="h3"
                                style={{
                                    fontWeight: 600,
                                    color:
                                        currentBalance >= 0
                                            ? theme.palette.success.dark
                                            : theme.palette.error.dark,
                                }}
                            >
                                {formatNumber(currentBalance)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* Calculation Section */}
                <Card elevation={1} style={{ marginBottom: theme.spacing(2) }}>
                    <CardContent>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                        >
                            <Box>
                                <Typography
                                    variant="h6"
                                    style={{ fontWeight: 600, marginBottom: 8 }}
                                >
                                    Monthly Token Grant Calculation
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={handleEditClick}
                                title="Edit token per unit allocation"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Divider style={{ margin: '12px 0' }} />
                        <Box
                            display="flex"
                            alignItems="center"
                            flexWrap="wrap"
                            style={{ gap: theme.spacing(1) }}
                        >
                            <Typography variant="body1">
                                <strong>Billable Units:</strong>{' '}
                                {formatNumber(billableUnits)}
                            </Typography>
                            <Typography
                                variant="body1"
                                style={{ margin: '0 8px' }}
                            >
                                ×
                            </Typography>
                            <Typography variant="body1">
                                <strong>Token Per Unit Allocation:</strong>{' '}
                                {formatNumber(tokensPerUnit)}
                            </Typography>
                            <Typography
                                variant="body1"
                                style={{ margin: '0 8px' }}
                            >
                                =
                            </Typography>
                            <Typography
                                variant="body1"
                                style={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                <strong>Monthly Token Grant:</strong>{' '}
                                {formatNumber(monthlyTokenGrant)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <Box
                    display="flex"
                    flexWrap="wrap"
                    style={{ gap: theme.spacing(2) }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setGrantDialogOpen(true)}
                        size="large"
                    >
                        Grant Tokens
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<RemoveIcon />}
                        onClick={() => setSubtractDialogOpen(true)}
                        size="large"
                        disabled={currentBalance <= 0}
                    >
                        Subtract Tokens
                    </Button>
                </Box>

                {/* Edit Tokens Per Unit Dialog */}
                <Dialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Edit Token Per Unit Allocation</DialogTitle>
                    <DialogContent>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginBottom: 16 }}
                        >
                            Update the token allocation per billable unit for{' '}
                            {property.name}
                        </Typography>
                        <TextField
                            fullWidth
                            label="Token Per Unit Allocation"
                            type="number"
                            value={editTokensPerUnit}
                            onChange={(e) =>
                                setEditTokensPerUnit(e.target.value)
                            }
                            variant="outlined"
                            margin="normal"
                            helperText="Number of tokens allocated per billable unit (default: 15)"
                            inputProps={{ min: 0 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setEditDialogOpen(false)}
                            color="primary"
                            startIcon={<CancelIcon />}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveTokensPerUnit}
                            color="primary"
                            variant="contained"
                            disabled={updateTokensPerUnit.isLoading}
                            startIcon={<SaveIcon />}
                        >
                            {updateTokensPerUnit.isLoading
                                ? 'Saving...'
                                : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Grant Tokens Dialog */}
                <Dialog
                    open={grantDialogOpen}
                    onClose={() => {
                        setGrantDialogOpen(false)
                        setGrantAmount('')
                        setGrantComment('')
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Grant Tokens</DialogTitle>
                    <DialogContent>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginBottom: 16 }}
                        >
                            Grant additional tokens to {property.name}
                        </Typography>
                        <TextField
                            fullWidth
                            label="Token Amount"
                            type="number"
                            value={grantAmount}
                            onChange={(e) => setGrantAmount(e.target.value)}
                            variant="outlined"
                            margin="normal"
                            helperText="Enter the number of tokens to grant"
                            inputProps={{ min: 1 }}
                        />
                        <TextField
                            fullWidth
                            label="Comment (Optional)"
                            value={grantComment}
                            onChange={(e) => setGrantComment(e.target.value)}
                            variant="outlined"
                            margin="normal"
                            multiline
                            rows={3}
                            helperText="Optional note about this token grant"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setGrantDialogOpen(false)
                                setGrantAmount('')
                                setGrantComment('')
                            }}
                            color="primary"
                            startIcon={<CancelIcon />}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGrantTokens}
                            color="primary"
                            variant="contained"
                            disabled={grantTokens.isLoading}
                            startIcon={<AddIcon />}
                        >
                            {grantTokens.isLoading
                                ? 'Granting...'
                                : 'Grant Tokens'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Subtract Tokens Dialog */}
                <Dialog
                    open={subtractDialogOpen}
                    onClose={() => {
                        setSubtractDialogOpen(false)
                        setSubtractAmount('')
                        setSubtractComment('')
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Subtract Tokens</DialogTitle>
                    <DialogContent>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginBottom: 16 }}
                        >
                            Subtract tokens from {property.name}. Current
                            balance: {formatNumber(currentBalance)} tokens
                        </Typography>
                        <TextField
                            fullWidth
                            label="Token Amount"
                            type="number"
                            value={subtractAmount}
                            onChange={(e) => setSubtractAmount(e.target.value)}
                            variant="outlined"
                            margin="normal"
                            helperText={`Enter the number of tokens to subtract (max: ${formatNumber(
                                currentBalance,
                            )})`}
                            inputProps={{ min: 1, max: currentBalance }}
                        />
                        <TextField
                            fullWidth
                            label="Comment (Optional)"
                            value={subtractComment}
                            onChange={(e) => setSubtractComment(e.target.value)}
                            variant="outlined"
                            margin="normal"
                            multiline
                            rows={3}
                            helperText="Optional note about this token subtraction"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setSubtractDialogOpen(false)
                                setSubtractAmount('')
                                setSubtractComment('')
                            }}
                            color="primary"
                            startIcon={<CancelIcon />}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubtractTokens}
                            color="secondary"
                            variant="contained"
                            disabled={subtractTokens.isLoading}
                            startIcon={<RemoveIcon />}
                        >
                            {subtractTokens.isLoading
                                ? 'Subtracting...'
                                : 'Subtract Tokens'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    )
}
